'use strict';

namespace Core.Customer {

    import CrossSellType = Core.Booking.CrossSellType;
    import BookedTrip = Core.ManageBooking.BookedTrip;
    import TripStage = Core.ManageBooking.TripStage;
    import DestinationContentCard = Core.Destinations.DestinationContentCard;

    angular
        .module('core')
        .service('customerDashboardService', CustomerDashboardService);

    export interface CustomerDashboardService {
        rehydrateHotelForHotelCard(estabId: number): ng.IPromise<Hotels.StaticHotel>;
        getRecentSearches(max: number): ng.IPromise<any[]>;
        getLastViewedHotel(): ng.IPromise<any>;
        getAllCrossSellOptions(bookingId: number): ng.IPromise<CrossSellOption[]>;
        getTemplateForDashboard(trip: BookedTrip|null): string;
        getTemplateForPlaceholder(type: DashboardTileType): string;
        trackDashboardLoad(trip: BookedTrip|null, tiles: any): void;
        trackDashboardClick(position: number, type: DashboardTileType): void;
        getDestinationExcludingSearch(search: any, destinations: DestinationContentCard[]): DestinationContentCard;
    }

    function CustomerDashboardService (
            $http: ng.IHttpService,
            $q: ng.IQService,
            $interpolate: ng.IInterpolateService,
            $rootScope: any,
            flightSearch: any,
            hotelService: any,
            imageService: any,
            destinationService: any,
            crossSellCommonService: any,
            flightExtrasService: any,
            bookingActionLink: any,
            hotelRehydrator: any,
            savedSearchService: any,
            searchTypesService: any,
            recentHotels: any,
            bookingsManagerService: ManageBooking.BookingsManagerService,
            analytics: any
        ) : CustomerDashboardService {

        const service: CustomerDashboardService = {
            rehydrateHotelForHotelCard,
            getRecentSearches,
            getLastViewedHotel,
            getAllCrossSellOptions,
            getTemplateForDashboard,
            getTemplateForPlaceholder,
            trackDashboardLoad,
            trackDashboardClick,
            getDestinationExcludingSearch
        };

        function getAllCrossSellOptions(bookingId: number): ng.IPromise<CrossSellOption[]> {
            let promises: ng.IPromise<any>[] = [];

            promises.push(crossSellCommonService.getBookingCrossSell(bookingId));
            promises.push(flightExtrasService.getExtrasCrossSell(bookingId));

            let crossSellOptions: CrossSellOption[] = [];
            let hotelPromise: ng.IPromise<any> = $q.when();

            return $q.all(promises).then(([crossSells, flightExtras]) => {

                if (crossSells && crossSells.data) {

                    _.forOwn(crossSells.data, (product: any, key: string) => {

                        if (product && (key !== 'Priorities')) {

                            // build cross sell option
                            let type: CrossSellType = crossSellTypeByCrossSellKey[key],
                                minPrice: number = 0,
                                imageUrl: string = '',
                                title: string = '';

                            // this is the original product (required for onward journey to remain compatible with MYB)
                            product.CrossSellType = type;

                            if (type === CrossSellType.CarHire ||
                                type === CrossSellType.Parking ||
                                type === CrossSellType.Transfers) {
                                // use cheapest option
                                minPrice = _.minBy(product.CrossSells, (cs: any) => cs.Price.Value).Price.Value;
                            }

                            if (type === CrossSellType.Attractions) {
                                // use first attraction option
                                const cs: any = _.head(product.CrossSells);
                                minPrice = cs.AdultPrice.Value;
                                imageUrl = `/attractions/Handlers/crosssellimage.ashx?acsid=${cs.Id}`;
                                title = cs.Title;
                            }

                            if (type === CrossSellType.Hotels) {
                                // use first hotel option
                                const cs: any = _.head(product.CrossSells);
                                // we need to get additional hotel info before we return the cross sells
                                hotelPromise = rehydrateHotelForHotelCard(cs.EstabId);
                                return; //we will add this in after the promise completes as it is a different structure
                            }

                            crossSellOptions.push(
                                new CrossSellProductOption(
                                    bookingId,
                                    type,
                                    product.CrossSellType === CrossSellType.Transfers
                                        || product.CrossSellType === CrossSellType.Attractions,
                                    minPrice,
                                    imageUrl,
                                    product,
                                    title
                                )
                            );
                        }
                    });

                    // build cross sell options for flight extras
                    _.forEach(flightExtras, extra => {
                        crossSellOptions.push(
                            new CrossSellFlightExtraOption (
                                bookingId,
                                extra.CrossSellType,
                                true,
                                extra.Price.Value,
                                $interpolate(bookingActionLink.get()[extra.Name])(_.pick(extra, 'BookingId'))
                            )
                        );
                    });

                    return hotelPromise.then((hotel: Hotels.StaticHotel)  => {
                        if (hotel) {
                            const product = crossSells.data.Hotels;
                            const cs :any = _.find(product.CrossSells, { 'EstabId' : hotel.Id });

                            product.Url = cs.Url; // use hotel landing page Url

                            crossSellOptions.push(
                                new CrossSellHotelOption(
                                    bookingId,
                                    CrossSellType.Hotels,
                                    false,
                                    cs.Price.Value,
                                    '',
                                    product,
                                    hotel.Title,
                                    hotel
                                )
                            );

                        }
                        return crossSellOptions;
                    });

                }

            });

        }

        function getRecentSearches(max: number): ng.IPromise<any[]> {
            // Restrict search types to Hotels/Holidays/Flights
            const searchTypes = [
                searchTypesService.searchType.Flight,
                searchTypesService.searchType.Hotels,
                searchTypesService.searchType.Holidays
            ];
            return savedSearchService.getSavedSearches(searchTypes).then(searches => {
                return _.take(searches, max);
            });
        }

        function getLastViewedHotel(): ng.IPromise<any> {
            return recentHotels.getIds().then((ids: number[]) => {
                return ids.length > 0 ? rehydrateHotelForHotelCard(ids[0]) : $q.when();
            });
        }

        function rehydrateHotelForHotelCard(estabId: number): ng.IPromise<Hotels.StaticHotel> {
            return hotelRehydrator.hotels([estabId], {
                fields: [
                    'Id',
                    'Title',
                    'Province',
                    'Country',
                    'StarRating',
                    'CustomerRating',
                    'CustomerRatingTitle',
                    'CustomerRatingCount',
                    'Url',
                    'IndicativeMinPrice'
                ],
                imageWidth: TRPCONFIG.DeviceSettings.HotelAvailabilityImageSize,
                imageHeight: TRPCONFIG.DeviceSettings.HotelAvailabilityImageSize
            }).then((hotels: Hotels.StaticHotel[]) => {
                return hotels[0];
            });
        }

        function getTemplateForDashboard(trip: BookedTrip|null): string {
            const templateSuffix = trip
                    ? TripStage[trip.TripStage]
                    : 'NoTrip';
            return `partials/customer/CustomerDashboard${templateSuffix}`;
        }

        function getTemplateForPlaceholder(type: DashboardTileType): string {
            return `partials/customer/Placeholder${DashboardTileType[type]}`;
        }

        function trackDashboardLoad(trip: BookedTrip|null, tiles: any): void {
            const tripData = trip
                ? {
                    MainBookingId: trip.MainBookingId,
                    TripStage: TripStage[trip.TripStage],
                    HasFlight: trip.FlightBooking ? true : false,
                    HasHotel: trip.HotelBooking ? true : false,
                    HasTransfer: trip.TransferBooking ? true : false,
                    HasCarHire: trip.CarHireBooking ? true : false,
                    HasAttractions: trip.AttractionsBooking ? true : false,
                    HasParking: trip.AirportParkingBooking ? true : false,
                    HasInsurance: trip.InsuranceBooking ? true : false
                }
                : null;
            const data = {
                SignedIn: $rootScope.user.SignedIn,
                Trip: tripData,
                Tiles: _.mapValues(tiles, (type: DashboardTileType) => {
                                return DashboardTileType[type];
                            })
            };
            analytics.track(
                'mixpanel',
                'CustomerDashboardLoad',
                data
            );
        }

        function trackDashboardClick(position: number, type: DashboardTileType): void {
            const data = {
                position: position,
                type: DashboardTileType[type]
            };
            analytics.track(
                'mixpanel',
                'CustomerDashboardClick',
                data
            );

        }

        function getDestinationExcludingSearch(
                search: any,
                destinations: DestinationContentCard[]
        ): DestinationContentCard {
            let searchDestination: any = _.get(search, 'DisplayDetails.Destinations[0]');
            if (!searchDestination && destinations) {
                return destinations[0];
            }
            // return the first destination which doesn't match the destination of the search
            return _.find(destinations, (dest: DestinationContentCard) => {
                return dest.Id !== searchDestination.Id && dest.Type !== searchDestination.Type;
            });
        }

        return service;
    }
}
